import { DIRECT_CHAT_ROOM } from "@circle-react/components/constants";
import { RailBarPortal } from "@circle-react/components/Layout/LegacyBridge";
import { PROFILE_PAGES } from "@circle-react/helpers/memberHelper";
import { useChatParticipantsApi } from "@circle-react/hooks/chatsV2/useChatParticipantsApi";
import { useRailbar } from "@circle-react/hooks/chatsV2/useRailbar";
import type { ProfileResponse } from "@circle-react/hooks/profile/useProfileInfo";
import { useProfileInfo } from "@circle-react/hooks/profile/useProfileInfo";
import { useElementHeight } from "@circle-react/hooks/utils/useElementHeight";

import { t } from "@/i18n-js/instance";
import { Rail } from "@/react/components/shared/uikit/Rail";
import type { ChatRoom } from "@/react/types/Chat/ChatRoom";

import { Content } from "./Content";
import { Header } from "./Header/Header";

const params = {
  page_name: PROFILE_PAGES.PROFILE_VIEW,
};

interface RailBarProps {
  chatRoom: ChatRoom;
  chatRoomUuid: string;
  refetchChatRoom: () => void;
}

const RailBar = ({ chatRoom, chatRoomUuid, refetchChatRoom }: RailBarProps) => {
  const {
    chat_room_name,
    chat_room_kind,
    chat_room_description,
    other_participants_preview: otherParticipants,
  } = chatRoom || {};

  const isDirect = chat_room_kind === DIRECT_CHAT_ROOM;
  const otherParticipantPublicId =
    otherParticipants?.[0]?.user_public_uid || "";

  const {
    isLoading: isChatParticipantsLoading,
    totalCount: totalParticipantsCount,
  } = useChatParticipantsApi(chatRoomUuid, {
    enabled: !isDirect,
  });

  const { toggleShowRailBar } = useRailbar();
  const {
    getProfileInfoQuery: {
      data: member = {} as ProfileResponse,
      isInitialLoading: isProfileLoading,
      refetch: refetchProfileInfo,
    },
  } = useProfileInfo({
    publicId: isDirect ? otherParticipantPublicId : undefined,
    params,
  });

  const { ref: headerRef, height: headerHeight } = useElementHeight();

  // The V4 compact profile draws its own close action, so the rail header would
  // only duplicate it.
  const shouldHideRailHeader = isDirect;

  return (
    <RailBarPortal
      isLoading={isDirect ? isProfileLoading : isChatParticipantsLoading}
      loaderVariant={isDirect ? "rail-profile" : "rail-member-list"}
    >
      {!shouldHideRailHeader && (
        <div ref={headerRef}>
          <Rail.Header>
            <Header
              title={isDirect ? t("profile") : chat_room_name}
              memberCount={totalParticipantsCount}
              isDirect={isDirect}
              description={chat_room_description}
              onClose={toggleShowRailBar}
            />
          </Rail.Header>
        </div>
      )}
      <Rail.Content
        noPadding
        heightGap={shouldHideRailHeader ? 0 : headerHeight}
      >
        <Content
          chatRoom={chatRoom}
          isDirect={isDirect}
          member={member}
          refetchChatRoom={refetchChatRoom}
          refetchProfileInfo={refetchProfileInfo}
          description={chat_room_description}
          onClose={toggleShowRailBar}
        />
      </Rail.Content>
    </RailBarPortal>
  );
};

export { RailBar };
